/**
 * AppointmentConfirmationsAdmin.tsx — Admin > Appointment Confirmations.
 *
 * Two tabs, as the corp dashboard's page:
 *   - Settings: this practice's send times, arrival-time mode and booking
 *     text (a settings form, not a list — left as it was);
 *   - Confirmations: corp's Confirmation Templates tab for this account
 *     (corp components/appointment-confirmations-table.tsx), laid out like
 *     every Admin list (Lead Sources is the reference —
 *     components/admin/AdminList.tsx): corp's columns less Account (Name,
 *     Send Type, Schedule; Name and Send Type sort from their titles, by name
 *     to start). Search and Show deleted in the toolbar; "Add Confirmation"
 *     top right while this tab is open; a click on a live row opens corp's
 *     edit dialog (AppointmentConfirmationFormModal); Edit / Delete on a live
 *     row, Restore on a deleted one (amber, struck through). Delete asks first
 *     and only turns the confirmation off.
 * On a Locked page (plan 2026-10-10-001: only the send settings change) the
 * Settings tab still saves; the Confirmations tab has no Add or row icons, and
 * a confirmation opens as a viewer.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { SlidersHorizontal, MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Tabs } from '@/components/ui/Tabs';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AppointmentConfirmationFormModal } from '@/components/admin/AppointmentConfirmationFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { toast } from '@/store/toastStore';
import { getTimezoneAbbreviation } from '@/lib/facilityTimezone';
import { getErrorMessage } from '@/services/apiClient';
import * as adminSvc from '@/services/admin.service';
import type { AppointmentConfirmation, ConfirmationSettings } from '@/services/admin.service';

// ─── Constants ────────────────────────────────────────────────────────────────

const SEND_TYPE_LABELS: Record<number, string> = { 0: 'SMS', 1: 'Email', 2: 'Email & SMS' };

function buildTimeOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    const hh = String(h).padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    opts.push({ value: `${hh}:00:00`, label: `${display}:00 ${period}` });
  }
  return opts;
}

const TIME_OPTIONS = buildTimeOptions();

// The corp Confirmation Templates columns (less Account), in corp order.
const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'send_type', header: 'Send Type', className: 'w-[15%]', sortable: true },
  { key: 'schedule', header: 'Schedule', className: 'w-[35%]' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** Name is the list's own order (as corp); ties keep it. */
const SORT_COMPARE: ListSortCompare<AppointmentConfirmation> = {
  name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  send_type: (a, b) => compareText(SEND_TYPE_LABELS[a.send_type] ?? 'Unknown', SEND_TYPE_LABELS[b.send_type] ?? 'Unknown'),
};

function scheduleSummary(item: AppointmentConfirmation): string {
  const days: string[] = [];
  if (item.day8) days.push('7d before');
  if (item.day7) days.push('6d before');
  if (item.day6) days.push('5d before');
  if (item.day5) days.push('4d before');
  if (item.day4) days.push('3d before');
  if (item.day3) days.push('2d before');
  if (item.day2) days.push('1d before');
  if (item.day1) days.push('Day of appt');
  return days.length > 0 ? days.join(', ') : 'None';
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AppointmentConfirmationsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [items, setItems] = useState<AppointmentConfirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('settings');
  /** The confirmation open in the form; 'new' to add one. */
  const [editItem, setEditItem] = useState<AppointmentConfirmation | 'new' | null>(null);
  const [deleteItem, setDeleteItem] = useState<AppointmentConfirmation | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountTimezone, setAccountTimezone] = useState<string | null>(null);

  const [settings, setSettings] = useState<ConfirmationSettings>({
    apptConfirmTime: '08:00:00',
    apptEmailConfirmTime: '08:00:00',
    apptConfirmUseArrivalTime: 1,
    apptBookedSmsSend: 0,
    apptBookedSmsMessage: '',
    apptBookedSmsDefaultMessage: '',
    apptConfirmedReplySmsSend: 0,
    apptConfirmedReplySmsMessage: '',
    apptConfirmedReplySmsDefaultMessage: '',
    apptGiFormSms: 0,
    // Assume set until the server says otherwise, so the "set a GI form first"
    // hint doesn't flash while loading. The server refuses enabling without one.
    giFormConfigured: true,
    apptGiFormSmsWording: '',
    accountTimezone: null,
  });
  const [savedSettings, setSavedSettings] = useState<ConfirmationSettings>(settings);
  const [savingSettings, setSavingSettings] = useState(false);
  // Save sends every setting on this page, so saving over the defaults after a
  // failed load would switch off whatever the practice had on. Blocked instead.
  const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);

  const tzAbbr = useMemo(
    () => (accountTimezone ? getTimezoneAbbreviation(accountTimezone) : ''),
    [accountTimezone],
  );

  const settingsDirty =
    settings.apptConfirmTime !== savedSettings.apptConfirmTime ||
    settings.apptEmailConfirmTime !== savedSettings.apptEmailConfirmTime ||
    settings.apptConfirmUseArrivalTime !== savedSettings.apptConfirmUseArrivalTime ||
    settings.apptBookedSmsSend !== savedSettings.apptBookedSmsSend ||
    settings.apptConfirmedReplySmsSend !== savedSettings.apptConfirmedReplySmsSend ||
    settings.apptGiFormSms !== savedSettings.apptGiFormSms;
  // apptBookedSmsMessage and apptConfirmedReplySmsMessage are intentionally
  // absent: the wording is read-only here and authored in the corp dashboard,
  // so it can never differ locally.

  // Deleted ones too, as corp loads them: Show deleted only filters.
  const loadConfirmations = useCallback(async () => {
    try {
      const result = await adminSvc.listAppointmentConfirmations({ includeDeleted: true });
      setItems(result.items);
      if (result.accountTimezone) {
        setAccountTimezone(result.accountTimezone);
      }
    } catch {
      toast.error('Failed to load appointment confirmations');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    await loadConfirmations();

    try {
      const sett = await adminSvc.getConfirmationSettings();
      setSettings(sett);
      setSavedSettings(sett);
      setSettingsLoadFailed(false);
      if (sett.accountTimezone) {
        setAccountTimezone(sett.accountTimezone);
      }
    } catch {
      setSettingsLoadFailed(true);
      toast.error('Failed to load confirmation settings');
    }

    setLoading(false);
  }, [loadConfirmations]);

  useEffect(() => {
    void load();
  }, [load]);

  // Listed by name, as corp lists them.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items
      .filter((i) => showDeleted || i.active)
      .filter(
        (i) =>
          !q ||
          i.name.toLowerCase().includes(q) ||
          (SEND_TYPE_LABELS[i.send_type] ?? '').toLowerCase().includes(q),
      )
      .sort(SORT_COMPARE.name);
  }, [items, searchQuery, showDeleted]);

  const sorting = useListSort(filtered, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [searchQuery, showDeleted] });

  function setActive(id: number, active: boolean) {
    setItems((prev) => prev.map((i) => (i.appointmentConfirmTypeID === id ? { ...i, active } : i)));
  }

  async function handleDelete() {
    if (!deleteItem) return;
    const item = deleteItem;
    setDeleteItem(null);
    try {
      await adminSvc.deleteAppointmentConfirmation(item.appointmentConfirmTypeID);
      setActive(item.appointmentConfirmTypeID, false);
      toast.success(`"${item.name}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The confirmation could not be deleted.'));
    }
  }

  async function handleRestore(item: AppointmentConfirmation) {
    try {
      await adminSvc.restoreAppointmentConfirmation(item.appointmentConfirmTypeID);
      setActive(item.appointmentConfirmTypeID, true);
      toast.success(`"${item.name}" restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The confirmation could not be restored.'));
    }
  }

  async function handleSaveSettings() {
    if (settingsLoadFailed) return;
    setSavingSettings(true);
    try {
      // Explicit subset, not the whole settings object: the endpoint reads each
      // key with has(), so omitting apptBookedSmsMessage means this page can
      // never overwrite wording it only displays. (Enabling with no wording
      // still gets the default seeded server-side, which comes back in the
      // echo below.)
      const payload: Partial<ConfirmationSettings> = {
        apptConfirmTime: settings.apptConfirmTime,
        apptEmailConfirmTime: settings.apptEmailConfirmTime,
        apptConfirmUseArrivalTime: settings.apptConfirmUseArrivalTime,
        apptBookedSmsSend: settings.apptBookedSmsSend,
        apptGiFormSms: settings.apptGiFormSms,
      };
      // Same rule for the confirmed-reply thank-you: switch only, never
      // apptConfirmedReplySmsMessage. And the switch only when it was changed
      // here: the corp dashboard edits it too, so sending the value this page
      // loaded would undo a change made there since.
      if (settings.apptConfirmedReplySmsSend !== savedSettings.apptConfirmedReplySmsSend) {
        payload.apptConfirmedReplySmsSend = settings.apptConfirmedReplySmsSend >= 1 ? 1 : 0;
      }
      const updated = await adminSvc.updateConfirmationSettings(payload);
      // Merge, don't replace: the response echoes only what was sent plus the
      // server-owned fields, so replacing would drop anything it left out.
      // Also picks up the default the server seeds when the booking text is
      // enabled with an empty body, so the textarea shows what will be sent.
      setSettings((s) => ({ ...s, ...updated }));
      setSavedSettings((s) => ({ ...s, ...updated }));
      toast.success('Confirmation settings updated');
    } catch (err) {
      // The server's sentence when it gives one (a 422 says what to change, e.g.
      // turning the GI text on without a GI form set); the form keeps what the
      // user set so they can retry without redoing it.
      toast.error(getErrorMessage(err, 'Confirmation settings were not saved. Try again.'));
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <AdminListPage
      title="Appointment Confirmations"
      subtitle="Confirmation settings for this practice, and the confirmation types in use."
      // Adds to the Confirmations tab, so it shows only there (AdminListPage hides it at Locked).
      actions={
        activeTab === 'confirmations' ? (
          <Button onClick={() => setEditItem('new')} leftIcon={<Plus className="h-4 w-4" />}>
            Add Confirmation
          </Button>
        ) : undefined
      }
    >
      {/*
        Two views, mirroring the corp dashboard's tabs so the same feature reads
        the same way in both apps: Settings is "how does this practice send", the
        Confirmations list is "what gets sent".
      */}
      <Tabs
        className="flex-1 min-h-0"
        onChange={setActiveTab}
        tabs={[
          {
            id: 'settings',
            label: 'Settings',
            icon: <SlidersHorizontal className="h-4 w-4" />,
            content: (
              <div className="flex-1 min-h-0 overflow-y-auto pt-4">
                {/* Send Time Settings */}
                <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Send Time Settings</h3>
                  {settingsLoadFailed && (
                    <p className="text-sm text-amber-700 mb-3">
                      These settings couldn&rsquo;t be loaded, so they can&rsquo;t be saved. Reload the page to try again.
                    </p>
                  )}
                  <div className="flex flex-wrap items-end gap-6">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-600 whitespace-nowrap">
                        SMS begins sending at
                      </label>
                      <select
                        className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={settings.apptConfirmTime}
                        onChange={(e) =>
                          setSettings((s) => ({ ...s, apptConfirmTime: e.target.value }))
                        }
                      >
                        {TIME_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-400 font-medium">{tzAbbr}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-600 whitespace-nowrap">
                        Email begins sending at
                      </label>
                      <select
                        className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={settings.apptEmailConfirmTime}
                        onChange={(e) =>
                          setSettings((s) => ({ ...s, apptEmailConfirmTime: e.target.value }))
                        }
                      >
                        {TIME_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-400 font-medium">{tzAbbr}</span>
                    </div>

                  </div>

                  <div className="border-t border-slate-200 mt-4 pt-4">
                    <Checkbox
                      checked={settings.apptConfirmUseArrivalTime === 1}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          apptConfirmUseArrivalTime: e.target.checked ? 1 : 0,
                        }))
                      }
                      label="Send one confirmation per visit, using the first appointment's arrival time"
                      description="When off, every appointment gets its own confirmation showing its own time."
                    />
                  </div>

                  {/*
                    Booking acknowledgement. Deliberately a separate block from the
                    reminder schedule above: it fires the moment an appointment is
                    created, not on a day countdown, and it uses its own message so a
                    patient is never told to "reply to confirm" the second they book.
                    Patients who have turned off transactional SMS never receive it.
                  */}
                  <div className="border-t border-slate-200 mt-4 pt-4">
                    <Checkbox
                      checked={settings.apptBookedSmsSend >= 1}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          apptBookedSmsSend: e.target.checked ? 1 : 0,
                        }))
                      }
                      label="Text the patient as soon as an appointment is booked"
                      description="Only patients with transactional SMS turned on will receive it."
                    />

                    {settings.apptBookedSmsSend >= 1 && (
                      <div className="mt-3 ml-6 space-y-3">
                        <Checkbox
                          checked={settings.apptBookedSmsSend === 2}
                          onChange={(e) =>
                            setSettings((s) => ({
                              ...s,
                              apptBookedSmsSend: e.target.checked ? 2 : 1,
                            }))
                          }
                          label="Also when staff book on the patient's behalf"
                          description="When off, only appointments patients schedule themselves in the app send this text."
                        />

                        {/*
                          Read-only on purpose. The wording is authored centrally in the
                          corp dashboard so every practice's patient-facing copy is
                          reviewed in one place; the toggles above stay editable here
                          because when to send is the practice's call.
                        */}
                        <div>
                          <label className="block text-sm text-slate-600 mb-1">Message</label>
                          {settings.apptBookedSmsMessage.trim() !== '' ? (
                            <div className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700 whitespace-pre-wrap">
                              {settings.apptBookedSmsMessage}
                            </div>
                          ) : (
                            <div className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-500 italic">
                              {settings.apptBookedSmsDefaultMessage
                                ? `Default wording will be sent: “${settings.apptBookedSmsDefaultMessage}”`
                                : 'No wording set yet.'}
                            </div>
                          )}
                          <p className="text-xs text-slate-500 mt-1">
                            Managed centrally — contact your Prospera administrator to change this wording.
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Dates and times are shown in the appointment facility&rsquo;s timezone.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/*
                    Thank-you sent back when a guest replies YES to a confirmation
                    text. Same split as the booking text: the switch is the
                    practice's call, the wording is authored centrally in the corp
                    dashboard. The wording stays visible while the switch is off so
                    the practice can see what it would turn on.
                  */}
                  <div className="border-t border-slate-200 mt-4 pt-4">
                    <Checkbox
                      checked={settings.apptConfirmedReplySmsSend >= 1}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          apptConfirmedReplySmsSend: e.target.checked ? 1 : 0,
                        }))
                      }
                      label="Text guests a thank-you when they confirm by text"
                    />

                    <div className="mt-3 ml-6">
                      <label className="block text-sm text-slate-600 mb-1">Message</label>
                      {settings.apptConfirmedReplySmsMessage.trim() !== '' ? (
                        <div className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700 whitespace-pre-wrap">
                          {settings.apptConfirmedReplySmsMessage}
                        </div>
                      ) : (
                        <div className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-500 italic">
                          {settings.apptConfirmedReplySmsDefaultMessage
                            ? `Default wording will be sent: “${settings.apptConfirmedReplySmsDefaultMessage}”`
                            : 'No wording set yet.'}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        Managed centrally — contact your Prospera administrator to change this wording.
                      </p>
                    </div>
                  </div>

                  {/*
                    GI form text. Its own message and its own setting, independent
                    of the booking text above: a practice may want this without
                    texting on every booking. One fixed wording for every practice,
                    so it is shown, never edited. Transactional, like the booking
                    text. Can't be turned on until the practice has a GI form set
                    (the server refuses it too); left un-disabled when already on so
                    an admin can still turn it off after the form is removed.
                  */}
                  <div className="border-t border-slate-200 mt-4 pt-4">
                    <Checkbox
                      checked={settings.apptGiFormSms === 1}
                      disabled={!settings.giFormConfigured && settings.apptGiFormSms !== 1}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          apptGiFormSms: e.target.checked ? 1 : 0,
                        }))
                      }
                      label="Text the GI form link when it hasn't been filled out"
                      description="Sent when an appointment is booked and on reminder days, at most once a day. Only patients with transactional SMS turned on will receive it."
                    />
                    {!settings.giFormConfigured && (
                      <p className="text-xs text-amber-700 mt-1 ml-7">
                        Set a GI form for this practice first.
                      </p>
                    )}

                    <div className="mt-3 ml-6 space-y-3">
                      <div>
                        <label className="block text-sm text-slate-600 mb-1">Message</label>
                        <div className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700 whitespace-pre-wrap">
                          {settings.apptGiFormSmsWording}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          The same wording is used for every practice.
                        </p>
                      </div>
                    </div>
                  </div>

                  {settingsDirty && (
                    <div className="mt-4">
                      <Button
                        size="sm"
                        onClick={handleSaveSettings}
                        loading={savingSettings}
                        disabled={savingSettings || settingsLoadFailed}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>

              </div>
            ),
          },
          {
            id: 'confirmations',
            label: 'Confirmations',
            icon: <MessageSquare className="h-4 w-4" />,
            content: (
              <div className="flex-1 min-h-0 flex flex-col pt-4">
                <AdminListToolbar
                  className="flex-shrink-0"
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder="Search by name or send type…"
                  searchLabel="Search appointment confirmations"
                  showDeleted={showDeleted}
                  onShowDeletedChange={setShowDeleted}
                  showDeletedId="appointment-confirmations-show-deleted"
                />

                {loading ? (
                  <SpinnerOverlay label="Loading appointment confirmations…" />
                ) : filtered.length === 0 ? (
                  <AdminListEmpty
                    title="No appointment confirmations found"
                    hint={
                      searchQuery.trim()
                        ? 'Try adjusting your search.'
                        : locked
                          ? undefined
                          : 'Add your first confirmation to get started.'
                    }
                  />
                ) : (
                  <>
                    <AdminListTable
                      columns={locked ? LOCKED_COLUMNS : COLUMNS}
                      aria-label="Appointment confirmations"
                      sort={sorting.sort}
                      onSortChange={sorting.onSortChange}
                    >
                      {pagination.pageItems.map((row) => {
                        const deleted = !row.active;
                        return (
                          <tr
                            key={row.appointmentConfirmTypeID}
                            className={adminRowClass({ deleted, clickable: !deleted })}
                            // A live row opens its form; a deleted one is restored, not edited.
                            onClick={deleted ? undefined : () => setEditItem(row)}
                          >
                            <td className={ADMIN_TD_CLASS}>
                              <AdminRowName deleted={deleted} nameTestId="appointment-confirmation-name">{row.name}</AdminRowName>
                            </td>
                            <td className={ADMIN_TD_CLASS}>
                              <span className="text-sm text-slate-500">
                                {SEND_TYPE_LABELS[row.send_type] ?? 'Unknown'}
                              </span>
                            </td>
                            <td className={ADMIN_TD_CLASS}>
                              <span className="text-sm text-slate-500">{scheduleSummary(row)}</span>
                            </td>
                            {!locked && (
                              <td className={ADMIN_TD_CLASS}>
                                <AdminRowActions>
                                  {deleted ? (
                                    <AdminRowAction kind="restore" label={`Restore ${row.name}`} onClick={() => void handleRestore(row)} />
                                  ) : (
                                    <>
                                      <AdminRowAction kind="edit" label={`Edit ${row.name}`} onClick={() => setEditItem(row)} />
                                      <AdminRowAction kind="delete" label={`Delete ${row.name}`} onClick={() => setDeleteItem(row)} />
                                    </>
                                  )}
                                </AdminRowActions>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </AdminListTable>
                    <PaginationFooter pagination={pagination} noun={['confirmation', 'confirmations']} />
                  </>
                )}
              </div>
            ),
          },
        ]}
      />

      {editItem && (
        <AppointmentConfirmationFormModal
          confirmation={editItem === 'new' ? null : editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => void loadConfirmations()}
        />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Confirmation"
        message={deleteItem ? `Are you sure you want to delete "${deleteItem.name}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => void handleDelete()}
        onClose={() => setDeleteItem(null)}
      />
    </AdminListPage>
  );
}
